import { cn } from "@/utils/theme";
import { Icon } from "@elaniintc/goes-design-system";
import type { ComponentProps } from "react";

interface DropdownListSeparatorProps {
  options: string[];
  icon?: ComponentProps<typeof Icon>;
  /**
   * "breadcrumb" (default): options rendered inline separated by chevrons.
   * "stacked": first option as a bold header row (with the icon and a rule),
   * the remaining options each on their own quiet line below.
   */
  variant?: "breadcrumb" | "stacked";
}

const DropdownListSeparator = ({
  options,
  className,
  icon,
  variant = "breadcrumb",
}: DropdownListSeparatorProps & ComponentProps<"div">) => {
  if (variant === "stacked") {
    const [head, ...rest] = options;
    return (
      <div className={cn("flex flex-col gap-1 px-2", className)}>
        <div className="flex gap-2 items-center">
          {icon && <Icon {...icon} />}
          <span className="text-xs font-semibold text-body truncate line-clamp-1">
            {head}
          </span>
          <span className="flex-auto border-t border-neutral-quiet"></span>
        </div>
        {rest.map((option) => (
          <span
            key={option}
            className="text-xs text-body-quiet truncate line-clamp-1"
          >
            {option}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={cn(`flex gap-2 items-center truncate px-2`, className)}>
      {icon && <Icon {...icon} />}
      <div className="flex items-center gap-2 flex-auto">
        {options.map((option, index) => (
          <div
            key={option}
            className="flex gap-2 items-center text-xs text-body-quiet truncate line-clamp-1 first:font-semibold first:text-body"
          >
            {option}
            {index < options.length - 1 && (
              <Icon type="far" name="chevron-right" />
            )}
          </div>
        ))}
        <span className="flex-auto border border-neutral-quiet"></span>
      </div>
    </div>
  );
};

export { DropdownListSeparator };
